import { useCallback, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { useTRPC } from "@/trpc";
import type { CredentialDocument } from "../types";

export type PreviewDocument = Pick<
  CredentialDocument,
  "id" | "title" | "fileName"
>;

/**
 * Open a credential document in the preview modal, and download it from there.
 *
 * Which endpoint is used depends on who is looking: an organization reading an
 * affiliated provider's file goes through the business-unit procedure, the
 * provider through their own. Both are mutations, because a signed URL is
 * minted rather than read — caching one in the query cache would keep handing
 * out a link long after it stopped working.
 *
 * Preview and download are two separate links: Supabase decides between inline
 * display and a save prompt from the `download` option at signing time, so the
 * download button mints its own rather than reusing the preview URL.
 */
export function useCredentialDocumentPreview({
  providerUserId,
  organizationBusinessUnitId,
  credentialViewId,
  expectedAffiliationId,
}: {
  providerUserId: string;
  /** Supplied by organization surfaces; absent when a provider reads their own. */
  organizationBusinessUnitId?: string;
  credentialViewId?: string;
  expectedAffiliationId?: string;
}) {
  const trpc = useTRPC();
  const [document, setDocument] = useState<PreviewDocument | null>(null);
  const [isReviewDocument, setIsReviewDocument] = useState(false);
  const [url, setUrl] = useState<string | null>(null);

  const forBu = useMutation(
    trpc.credentials.getDocumentUrlForBu.mutationOptions(),
  );
  const forSelf = useMutation(
    trpc.credentials.getMyDocumentUrl.mutationOptions(),
  );
  const reviewForBu = useMutation(
    trpc.credentials.getReviewDocumentUrlForBu.mutationOptions(),
  );
  const reviewForSelf = useMutation(
    trpc.credentials.getMyReviewDocumentUrl.mutationOptions(),
  );

  const mintUrl = useCallback(
    async ({
      documentId,
      asAttachment,
      reviewDocument,
    }: {
      documentId: string;
      asAttachment: boolean;
      reviewDocument: boolean;
    }) => {
      if (organizationBusinessUnitId && reviewDocument) {
        const result = await reviewForBu.mutateAsync({
          organizationBusinessUnitId,
          providerUserId,
          ...(expectedAffiliationId ? { expectedAffiliationId } : {}),
          documentId,
          asAttachment,
        });
        return result.signedUrl;
      }
      if (organizationBusinessUnitId) {
        const result = await forBu.mutateAsync({
          organizationBusinessUnitId,
          providerUserId,
          ...(expectedAffiliationId ? { expectedAffiliationId } : {}),
          credentialViewId,
          documentId,
          asAttachment,
        });
        return result.signedUrl;
      }
      if (reviewDocument) {
        const result = await reviewForSelf.mutateAsync({
          documentId,
          switchUserId: providerUserId,
          asAttachment,
        });
        return result.signedUrl;
      }
      const result = await forSelf.mutateAsync({
        documentId,
        switchUserId: providerUserId,
        asAttachment,
      });
      return result.signedUrl;
    },
    [
      forBu,
      forSelf,
      organizationBusinessUnitId,
      credentialViewId,
      expectedAffiliationId,
      providerUserId,
      reviewForBu,
      reviewForSelf,
    ],
  );

  /** The server's messages here are written for a person — a quarantined or
   * still-scanning file explains itself better than "something went wrong". */
  const reportFailure = (error: unknown) =>
    toast.error(
      error instanceof Error ? error.message : "Could not open this document",
    );

  async function open(next: CredentialDocument) {
    setDocument(next);
    setIsReviewDocument(false);
    setUrl(null);
    try {
      setUrl(
        await mintUrl({
          documentId: next.id,
          asAttachment: false,
          reviewDocument: false,
        }),
      );
    } catch (error) {
      reportFailure(error);
      setDocument(null);
    }
  }

  async function openReview(next: PreviewDocument) {
    setDocument(next);
    setIsReviewDocument(true);
    setUrl(null);
    try {
      setUrl(
        await mintUrl({
          documentId: next.id,
          asAttachment: false,
          reviewDocument: true,
        }),
      );
    } catch (error) {
      reportFailure(error);
      setDocument(null);
    }
  }

  async function download() {
    if (!document) {
      return;
    }
    try {
      const signedUrl = await mintUrl({
        documentId: document.id,
        asAttachment: true,
        reviewDocument: isReviewDocument,
      });
      window.open(signedUrl, "_blank", "noopener,noreferrer");
    } catch (error) {
      reportFailure(error);
    }
  }

  return {
    document,
    url,
    isLoading:
      forBu.isPending ||
      forSelf.isPending ||
      reviewForBu.isPending ||
      reviewForSelf.isPending,
    open,
    openReview,
    download,
    close: () => {
      setDocument(null);
      setUrl(null);
      setIsReviewDocument(false);
    },
  };
}
